import React from 'react';
import { useMutation } from '@apollo/client';
import TrackerAddQuickMealMutation from './TrackerAddQuickMealMutation';
import TrackerQuery from '../Fetch/TrackerQuery';
import FetchUserMealsQuery from '../../UserMeals/Fetch/FetchUserMealsQuery';
import { formatIsoDate } from '../../../Helpers/dateFormatter';
import type { AddQuickMealInput, QuickMealMutationResult } from './types';

export type AddQuickMealFn = (
  input: AddQuickMealInput,
  saveYourMeal?: boolean
) => Promise<QuickMealMutationResult>;

interface ITrackerAddQuickMealConnectorApolloProps {
  children: ({ addQuickMeal }: { addQuickMeal: AddQuickMealFn }) => React.ReactElement;
  post?: ((message: string) => void) | null;
}

/**
 * Same contract as `TrackerAddMealConnectorApollo`: a logged meal (`dateTime` set) refetches that
 * day's tracker, a template refetches the Your Meals list, and the webview is asked to reload unless
 * the caller still has to save the meal to Your Meals afterwards.
 */
const TrackerAddQuickMealConnectorApollo: React.FC<ITrackerAddQuickMealConnectorApolloProps> = ({
  children,
  post,
}) => {
  const [addQuickMealMutation] = useMutation(TrackerAddQuickMealMutation);

  const addQuickMeal: AddQuickMealFn = async (input, saveYourMeal = false) => {
    const response = await addQuickMealMutation({
      variables: { input },
      awaitRefetchQueries: true,
      refetchQueries: input.dateTime
        ? [{ query: TrackerQuery, variables: { date: formatIsoDate(new Date(input.dateTime)) } }]
        : [{ query: FetchUserMealsQuery }],
    });

    if (post && !saveYourMeal) {
      post('reload');
    }

    return response.data.dailyTracker.addQuickMeal;
  };

  return children({ addQuickMeal });
};

export default TrackerAddQuickMealConnectorApollo;
